import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  WarningOctagon, Plus, Trash, FloppyDisk, Scales, MagnifyingGlass, Sparkle,
  UploadSimple, FileXls, ArrowClockwise, Gavel, FileDoc, DownloadSimple,
} from "@phosphor-icons/react";
import { SlaPill } from "../components/SlaPill";

const ENTRY_POINTS = [
  ["self_report", "Self-Report"], ["investigation", "Investigation"], ["audit", "Audit"],
  ["spot_check", "Spot Check"], ["self_certification", "Self-Certification"],
  ["complaint", "Complaint"], ["other", "Other"],
];
const RISKS = [["minimal", "Minimal"], ["moderate", "Moderate"], ["serious", "Serious"]];
const DISPOSITIONS = [
  ["", "— Not set —"], ["pending", "Pending"], ["fft", "Find, Fix, Track (FFT)"],
  ["compliance_exception", "Compliance Exception"], ["settlement", "Settlement"],
  ["nop", "Notice of Penalty"], ["dismissed", "Dismissed"],
];
const VRFS = ["Lower", "Medium", "High"];
const VSLS = ["Lower", "Moderate", "High", "Severe"];

const STATUS_STYLE = {
  open: "bg-slate-100 text-slate-700 border-slate-300",
  mitigation: "bg-amber-100 text-amber-800 border-amber-300",
  enforcement: "bg-blue-100 text-blue-800 border-blue-300",
  closed: "bg-emerald-100 text-emerald-800 border-emerald-300",
};
const STATUS_LABEL = { open: "Open", mitigation: "Mitigation", enforcement: "Enforcement", closed: "Closed" };
const PRIORITY_STYLE = { high: "bg-red-100 text-red-700 border-red-300", medium: "bg-amber-100 text-amber-800 border-amber-300", low: "bg-slate-100 text-slate-600 border-slate-300" };
const usd = (n) => (n == null ? "—" : "$" + Math.round(n).toLocaleString());
const labelOf = (arr, v) => (arr.find(([k]) => k === v) || [null, v])[1];

export default function PncTracker() {
  const [tab, setTab] = useState("records");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/pnc").then(({ data }) => setRecords(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openRecord = async (id) => {
    try { const { data } = await api.get(`/pnc/${id}`); setSelected(data); }
    catch (e) { toast.error("Couldn't open that record."); }
  };

  const onSaved = (rec) => {
    setRecords((rs) => rs.map((r) => (r.id === rec.id ? rec : r)));
    setSelected(rec);
  };

  return (
    <div className="max-w-6xl mx-auto px-5 py-8 space-y-6" data-testid="pnc-page">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-primary/10 border border-primary/20"><WarningOctagon className="text-primary" size={22} /></div>
        <div className="flex-1">
          <h1 className="text-2xl font-display font-bold">PNC Tracker</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Track a Possible Non-Compliance end-to-end — from discovery or self-report, through your mitigation
            plan, to enforcement disposition — with a Sanction Guideline estimate and a benchmark against public
            NERC Notices of Penalty.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none">
          <TabsTrigger value="records" className="rounded-none" data-testid="pnc-tab-records">PNC Records</TabsTrigger>
          <TabsTrigger value="nop" className="rounded-none" data-testid="pnc-tab-nop">NOP Benchmark Library</TabsTrigger>
        </TabsList>

        <TabsContent value="records" className="mt-5 space-y-4">
          <div className="flex justify-between items-center">
            <div className="text-sm text-muted-foreground">{records.length} record{records.length === 1 ? "" : "s"}</div>
            <Button className="rounded-none" onClick={() => setShowNew(true)} data-testid="pnc-new-btn"><Plus size={16} className="mr-1.5" /> New PNC</Button>
          </div>
          {loading ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
          ) : records.length === 0 ? (
            <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="pnc-empty">
              No PNC records yet. Click <span className="font-semibold">New PNC</span> to log your first possible non-compliance.
            </div>
          ) : (
            <div className="space-y-2">
              {records.map((r) => (
                <button key={r.id} onClick={() => openRecord(r.id)} data-testid={`pnc-card-${r.id}`}
                  className="w-full text-left border border-border bg-white p-4 hover:border-primary transition-colors flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold truncate">{r.title}</span>
                      <Badge variant="outline" className={`rounded-none text-[11px] ${STATUS_STYLE[r.status] || ""}`}>{STATUS_LABEL[r.status] || r.status}</Badge>
                      {r.priority && <Badge variant="outline" className={`rounded-none text-[10px] uppercase ${PRIORITY_STYLE[r.priority] || ""}`}>{r.priority} priority</Badge>}
                      <SlaPill sla={r.sla} />
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 flex gap-3 flex-wrap">
                      {r.standard_id && <span className="font-mono">{r.standard_id} {r.requirement}</span>}
                      <span>{labelOf(ENTRY_POINTS, r.entry_point)}</span>
                      {r.discovery_date && <span>Discovered {r.discovery_date}</span>}
                      {r.risk_assessment && <span>Risk: {labelOf(RISKS, r.risk_assessment)}</span>}
                    </div>
                  </div>
                  {r.sanction_estimate && (
                    <div className="text-right shrink-0">
                      <div className="text-[10px] uppercase text-muted-foreground">Est. penalty</div>
                      <div className="text-sm font-semibold font-mono">{usd(r.sanction_estimate.low)}–{usd(r.sanction_estimate.high)}</div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="nop" className="mt-5">
          <NopLibrary />
        </TabsContent>
      </Tabs>

      {showNew && <NewPncDialog onClose={() => setShowNew(false)} onCreated={(rec) => { setRecords((rs) => [rec, ...rs]); setShowNew(false); setSelected(rec); }} />}
      {selected && <PncDetail record={selected} onClose={() => setSelected(null)} onSaved={onSaved}
        onDeleted={(id) => { setRecords((rs) => rs.filter((r) => r.id !== id)); setSelected(null); }} />}
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-xs">{label}</Label>{children}</div>;
}

function NewPncDialog({ onClose, onCreated }) {
  const [f, setF] = useState({ title: "", standard_id: "", requirement: "", entry_point: "self_report", discovery_date: "", description: "", risk_assessment: "", reliability_impact: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const create = async () => {
    if (!f.title.trim()) { toast.error("Give the PNC a title."); return; }
    setBusy(true);
    try { const { data } = await api.post("/pnc", f); toast.success("PNC record created."); onCreated(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't create the record."); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-lg" data-testid="pnc-new-dialog">
        <DialogHeader><DialogTitle>New Possible Non-Compliance</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Title *"><Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. CIP-007-6 R2 patch evaluation gap" className="rounded-none" data-testid="pnc-new-title" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Standard"><Input value={f.standard_id} onChange={(e) => set("standard_id", e.target.value)} placeholder="CIP-007-6" className="rounded-none" data-testid="pnc-new-standard" /></Field>
            <Field label="Requirement"><Input value={f.requirement} onChange={(e) => set("requirement", e.target.value)} placeholder="R2" className="rounded-none" data-testid="pnc-new-req" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Point of entry">
              <Select value={f.entry_point} onValueChange={(v) => set("entry_point", v)}>
                <SelectTrigger className="rounded-none" data-testid="pnc-new-entry"><SelectValue /></SelectTrigger>
                <SelectContent>{ENTRY_POINTS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Discovery date"><Input type="date" value={f.discovery_date || ""} onChange={(e) => set("discovery_date", e.target.value)} className="rounded-none" data-testid="pnc-new-date" /></Field>
          </div>
          <Field label="Risk assessment">
            <Select value={f.risk_assessment || "none"} onValueChange={(v) => set("risk_assessment", v === "none" ? "" : v)}>
              <SelectTrigger className="rounded-none" data-testid="pnc-new-risk"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="none">— Not set —</SelectItem>{RISKS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Description"><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={3} className="rounded-none" data-testid="pnc-new-desc" /></Field>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={create} disabled={busy} data-testid="pnc-new-save">{busy ? "Creating…" : "Create"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PncDetail({ record, onClose, onSaved, onDeleted }) {
  const [r, setR] = useState(record);
  const [busy, setBusy] = useState(false);
  const [showSR, setShowSR] = useState(false);
  useEffect(() => { setR(record); }, [record]);
  const set = (k, v) => setR((p) => ({ ...p, [k]: v }));

  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const payload = {
        title: r.title, standard_id: r.standard_id, requirement: r.requirement, entry_point: r.entry_point,
        discovery_date: r.discovery_date, description: r.description, risk_assessment: r.risk_assessment,
        reliability_impact: r.reliability_impact, root_cause: r.root_cause, milestones: r.milestones,
        mitigation_complete_date: r.mitigation_complete_date, disposition: r.disposition,
        tracking_number: r.tracking_number, final_penalty: r.final_penalty, closed_date: r.closed_date, ...extra,
      };
      const { data } = await api.put(`/pnc/${r.id}`, payload);
      setR(data); onSaved(data); toast.success("Saved.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed."); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this PNC record? This cannot be undone.")) return;
    try { await api.delete(`/pnc/${r.id}`); toast.success("Deleted."); onDeleted(r.id); }
    catch (e) { toast.error("Delete failed."); }
  };

  const milestones = r.milestones || [];
  const setMs = (ms) => set("milestones", ms);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="pnc-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6 flex-wrap">
            <span className="truncate">{r.title}</span>
            <Badge variant="outline" className={`rounded-none text-[11px] ${STATUS_STYLE[r.status] || ""}`}>{STATUS_LABEL[r.status] || r.status}</Badge>
            {r.priority && <Badge variant="outline" className={`rounded-none text-[10px] uppercase ${PRIORITY_STYLE[r.priority] || ""}`}>{r.priority} priority</Badge>}
            <SlaPill sla={r.sla} />
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="discovery">
          <TabsList className="rounded-none">
            <TabsTrigger value="discovery" className="rounded-none" data-testid="pnc-stage-discovery">1 · Discovery</TabsTrigger>
            <TabsTrigger value="mitigation" className="rounded-none" data-testid="pnc-stage-mitigation">2 · Mitigation</TabsTrigger>
            <TabsTrigger value="enforcement" className="rounded-none" data-testid="pnc-stage-enforcement">3 · Enforcement</TabsTrigger>
          </TabsList>

          {/* Discovery */}
          <TabsContent value="discovery" className="space-y-3 mt-4">
            <Field label="Title"><Input value={r.title} onChange={(e) => set("title", e.target.value)} className="rounded-none" data-testid="pnc-edit-title" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Standard"><Input value={r.standard_id || ""} onChange={(e) => set("standard_id", e.target.value)} placeholder="CIP-007-6" className="rounded-none" data-testid="pnc-edit-standard" /></Field>
              <Field label="Requirement"><Input value={r.requirement || ""} onChange={(e) => set("requirement", e.target.value)} placeholder="R2" className="rounded-none" data-testid="pnc-edit-req" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Point of entry">
                <Select value={r.entry_point} onValueChange={(v) => set("entry_point", v)}>
                  <SelectTrigger className="rounded-none" data-testid="pnc-edit-entry"><SelectValue /></SelectTrigger>
                  <SelectContent>{ENTRY_POINTS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Discovery date"><Input type="date" value={r.discovery_date || ""} onChange={(e) => set("discovery_date", e.target.value)} className="rounded-none" data-testid="pnc-edit-date" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Risk assessment">
                <Select value={r.risk_assessment || "none"} onValueChange={(v) => set("risk_assessment", v === "none" ? "" : v)}>
                  <SelectTrigger className="rounded-none" data-testid="pnc-edit-risk"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— Not set —</SelectItem>{RISKS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Reliability impact"><Input value={r.reliability_impact || ""} onChange={(e) => set("reliability_impact", e.target.value)} className="rounded-none" data-testid="pnc-edit-impact" /></Field>
            </div>
            <Field label="Description"><Textarea value={r.description || ""} onChange={(e) => set("description", e.target.value)} rows={4} className="rounded-none" data-testid="pnc-edit-desc" /></Field>
          </TabsContent>

          {/* Mitigation */}
          <TabsContent value="mitigation" className="space-y-3 mt-4">
            <Field label="Root cause"><Textarea value={r.root_cause || ""} onChange={(e) => set("root_cause", e.target.value)} rows={3} className="rounded-none" data-testid="pnc-edit-rootcause" /></Field>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Mitigation milestones</Label>
                <Button size="sm" variant="outline" className="rounded-none h-7" data-testid="pnc-add-milestone"
                  onClick={() => setMs([...milestones, { text: "", target_date: "", done: false }])}><Plus size={13} className="mr-1" /> Add</Button>
              </div>
              <div className="space-y-2">
                {milestones.length === 0 && <p className="text-xs text-muted-foreground">No milestones yet.</p>}
                {milestones.map((m, i) => (
                  <div key={m.id || i} className="flex items-center gap-2 border border-border p-2" data-testid={`pnc-milestone-${i}`}>
                    <Switch checked={!!m.done} onCheckedChange={(v) => setMs(milestones.map((x, j) => j === i ? { ...x, done: v } : x))} data-testid={`pnc-milestone-done-${i}`} />
                    <Input value={m.text} onChange={(e) => setMs(milestones.map((x, j) => j === i ? { ...x, text: e.target.value } : x))} placeholder="Milestone" className="rounded-none h-8 flex-1" data-testid={`pnc-milestone-text-${i}`} />
                    <Input type="date" value={m.target_date || ""} onChange={(e) => setMs(milestones.map((x, j) => j === i ? { ...x, target_date: e.target.value } : x))} className="rounded-none h-8 w-40" data-testid={`pnc-milestone-date-${i}`} />
                    <button onClick={() => setMs(milestones.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-red-600" data-testid={`pnc-milestone-remove-${i}`}><Trash size={16} /></button>
                  </div>
                ))}
              </div>
            </div>
            <Field label="Mitigation completion date"><Input type="date" value={r.mitigation_complete_date || ""} onChange={(e) => set("mitigation_complete_date", e.target.value)} className="rounded-none w-48" data-testid="pnc-edit-mitdate" /></Field>
          </TabsContent>

          {/* Enforcement */}
          <TabsContent value="enforcement" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Disposition">
                <Select value={r.disposition || ""} onValueChange={(v) => set("disposition", v)}>
                  <SelectTrigger className="rounded-none" data-testid="pnc-edit-disposition"><SelectValue placeholder="— Not set —" /></SelectTrigger>
                  <SelectContent>{DISPOSITIONS.filter(([k]) => k !== "").map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Tracking / NOC number"><Input value={r.tracking_number || ""} onChange={(e) => set("tracking_number", e.target.value)} className="rounded-none" data-testid="pnc-edit-tracking" /></Field>
              <Field label="Final penalty ($)"><Input type="number" value={r.final_penalty ?? ""} onChange={(e) => set("final_penalty", e.target.value === "" ? null : Number(e.target.value))} className="rounded-none" data-testid="pnc-edit-penalty" /></Field>
              <Field label="Closed date"><Input type="date" value={r.closed_date || ""} onChange={(e) => set("closed_date", e.target.value)} className="rounded-none" data-testid="pnc-edit-closed" /></Field>
            </div>

            <SanctionEstimate record={r} onEstimate={(snap) => setR((p) => ({ ...p, sanction_estimate: snap }))} />
            <NopBenchmark record={r} onAnalysis={(a) => setR((p) => ({ ...p, nop_analysis: a }))} />
          </TabsContent>
        </Tabs>

        <div className="flex justify-between items-center mt-4 border-t border-border pt-3">
          <Button variant="ghost" className="rounded-none text-red-600 hover:text-red-700" onClick={del} data-testid="pnc-delete"><Trash size={16} className="mr-1.5" /> Delete</Button>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => setShowSR(true)} data-testid="pnc-self-report-btn"><FileDoc size={16} className="mr-1.5" /> Self-Report</Button>
            <Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button>
            <Button className="rounded-none" onClick={() => save()} disabled={busy} data-testid="pnc-save"><FloppyDisk size={16} className="mr-1.5" /> {busy ? "Saving…" : "Save changes"}</Button>
          </div>
        </div>
        {showSR && <SelfReportDialog pncId={r.id} onClose={() => setShowSR(false)} />}
      </DialogContent>
    </Dialog>
  );
}

function SanctionEstimate({ record, onEstimate }) {
  const est = record.sanction_estimate;
  const [f, setF] = useState({
    vrf: est?.inputs?.vrf || "Medium", vsl: est?.inputs?.vsl || "High",
    days: est?.inputs?.days || 1, violations: est?.inputs?.violations || 1,
    self_report_pct: est?.inputs?.self_report_pct ?? (record.entry_point === "self_report" ? 30 : 0),
    cooperation_pct: est?.inputs?.cooperation_pct ?? 0, compliance_pct: est?.inputs?.compliance_pct ?? 0,
    settlement_pct: est?.inputs?.settlement_pct ?? 0, intentional: est?.inputs?.intentional || false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/pnc/${record.id}/sanction-estimate`, f); onEstimate(data); toast.success("Sanction estimate saved to this PNC."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't compute the estimate."); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-border bg-slate-50 p-4 space-y-3" data-testid="pnc-sanction">
      <div className="flex items-center gap-2"><Scales size={18} className="text-primary" /><h4 className="font-semibold text-sm">Sanction Guideline estimate (Appendix 4B)</h4></div>
      <div className="grid sm:grid-cols-3 gap-2">
        <Field label="VRF"><Select value={f.vrf} onValueChange={(v) => set("vrf", v)}><SelectTrigger className="rounded-none h-9" data-testid="pnc-sanction-vrf"><SelectValue /></SelectTrigger><SelectContent>{VRFS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select></Field>
        <Field label="VSL"><Select value={f.vsl} onValueChange={(v) => set("vsl", v)}><SelectTrigger className="rounded-none h-9" data-testid="pnc-sanction-vsl"><SelectValue /></SelectTrigger><SelectContent>{VSLS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select></Field>
        <div />
        <Field label="Days"><Input type="number" min={1} value={f.days} onChange={(e) => set("days", Number(e.target.value))} className="rounded-none h-9" data-testid="pnc-sanction-days" /></Field>
        <Field label="Violations"><Input type="number" min={1} value={f.violations} onChange={(e) => set("violations", Number(e.target.value))} className="rounded-none h-9" data-testid="pnc-sanction-violations" /></Field>
        <div />
        <Field label="Self-report −% (0–30)"><Input type="number" min={0} max={30} value={f.self_report_pct} onChange={(e) => set("self_report_pct", Number(e.target.value))} className="rounded-none h-9" data-testid="pnc-sanction-selfreport" /></Field>
        <Field label="Cooperation −% (0–20)"><Input type="number" min={0} max={20} value={f.cooperation_pct} onChange={(e) => set("cooperation_pct", Number(e.target.value))} className="rounded-none h-9" data-testid="pnc-sanction-coop" /></Field>
        <Field label="Compliance prog −% (0–40)"><Input type="number" min={0} max={40} value={f.compliance_pct} onChange={(e) => set("compliance_pct", Number(e.target.value))} className="rounded-none h-9" data-testid="pnc-sanction-compliance" /></Field>
      </div>
      <label className="flex items-center gap-2 text-sm"><Switch checked={f.intentional} onCheckedChange={(v) => set("intentional", v)} data-testid="pnc-sanction-intentional" /> Intentional violation (×2)</label>
      <div className="flex items-center justify-between">
        <Button size="sm" className="rounded-none" onClick={run} disabled={busy} data-testid="pnc-sanction-run"><Gavel size={15} className="mr-1.5" /> {busy ? "Calculating…" : "Estimate & save"}</Button>
        <a href="/sanction-calculator" target="_blank" rel="noreferrer" className="text-xs text-primary underline">Open full calculator ↗</a>
      </div>
      {est && (
        <div className="text-center py-2 bg-white border border-primary/20" data-testid="pnc-sanction-result">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Estimated penalty range</div>
          <div className="text-xl font-display font-bold text-primary">{usd(est.low)} – {usd(est.high)}</div>
          {est.computed_at && <div className="text-[11px] text-muted-foreground">Saved {new Date(est.computed_at).toLocaleDateString()}</div>}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">Planning estimate only — actual penalties are set at NERC / Regional Entity discretion.</p>
    </div>
  );
}

function NopBenchmark({ record, onAnalysis }) {
  const [sim, setSim] = useState(null);
  const [busy, setBusy] = useState("");
  const analysis = record.nop_analysis;

  const findSimilar = async () => {
    setBusy("find");
    try { const { data } = await api.get(`/pnc/${record.id}/similar`); setSim(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't search the library."); }
    finally { setBusy(""); }
  };
  const runAi = async () => {
    setBusy("ai");
    try { const { data } = await api.post(`/pnc/${record.id}/similar/ai`); onAnalysis(data); toast.success("AI benchmark ready."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI benchmark failed."); }
    finally { setBusy(""); }
  };

  return (
    <div className="border border-border bg-white p-4 space-y-3" data-testid="pnc-nop-benchmark">
      <div className="flex items-center gap-2"><MagnifyingGlass size={18} className="text-primary" /><h4 className="font-semibold text-sm">Similar NERC penalties (benchmark)</h4></div>
      <p className="text-[11px] text-muted-foreground">Compares against public Notice of Penalty spreadsheets you've uploaded in the <span className="font-semibold">NOP Benchmark Library</span> tab. Matched by standard family (e.g. CIP-007).</p>
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" variant="outline" className="rounded-none" onClick={findSimilar} disabled={busy === "find"} data-testid="pnc-find-similar"><MagnifyingGlass size={14} className="mr-1.5" /> {busy === "find" ? "Searching…" : "Find similar filings"}</Button>
        <Button size="sm" variant="outline" className="rounded-none" onClick={runAi} disabled={busy === "ai"} data-testid="pnc-ai-benchmark"><Sparkle size={14} className="mr-1.5" /> {busy === "ai" ? "Analyzing…" : "AI benchmark analysis"}</Button>
      </div>

      {sim && (
        <div data-testid="pnc-similar-result">
          {sim.total_library === 0 ? (
            <p className="text-xs text-amber-700">Your NOP library is empty. Upload NERC penalty spreadsheets in the NOP Benchmark Library tab first.</p>
          ) : !sim.standard_base ? (
            <p className="text-xs text-amber-700">Set a Standard (e.g. CIP-007-6) on this PNC to benchmark it.</p>
          ) : !sim.stats ? (
            <p className="text-xs text-muted-foreground">No comparable filings for <span className="font-mono">{sim.standard_base}</span> in your library ({sim.total_library} total records).</p>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-4 gap-2 text-center">
                <Stat label="Matches" value={sim.stats.count} />
                <Stat label="Median" value={usd(sim.stats.median)} />
                <Stat label="Range" value={`${usd(sim.stats.min)}–${usd(sim.stats.max)}`} />
                <Stat label="$0 filings" value={sim.stats.zero_dollar} />
              </div>
              <div className="max-h-48 overflow-y-auto border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-muted sticky top-0"><tr><th className="text-left p-1.5">Standard</th><th className="text-left p-1.5">Req</th><th className="text-right p-1.5">Penalty</th><th className="text-left p-1.5">Risk</th><th className="text-left p-1.5">Disposition</th></tr></thead>
                  <tbody>
                    {sim.matches.map((m, i) => (
                      <tr key={i} className="border-t border-border"><td className="p-1.5 font-mono">{m.standard}</td><td className="p-1.5">{m.requirement}</td><td className="p-1.5 text-right font-mono">{usd(m.penalty)}</td><td className="p-1.5">{m.risk}</td><td className="p-1.5">{m.disposition}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {analysis && (
        <div className="border-l-2 border-primary bg-primary/5 p-3" data-testid="pnc-ai-analysis">
          <div className="text-[11px] uppercase text-muted-foreground mb-1">AI benchmark · based on {analysis.based_on} records</div>
          <div className="text-sm whitespace-pre-wrap">{analysis.text}</div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return <div className="border border-border p-2"><div className="text-[10px] uppercase text-muted-foreground">{label}</div><div className="text-sm font-semibold font-mono">{value}</div></div>;
}

const SR_FIELDS = [
  ["entity_name", "Registered Entity", false], ["ncr_id", "NCR ID", false],
  ["regional_entity", "Regional Entity", false], ["registered_functions", "Registered Function(s)", false],
  ["standard", "Reliability Standard", false], ["requirement", "Requirement", false],
  ["discovery_method", "Method of Discovery", false], ["discovery_date", "Date of Discovery", false],
  ["duration", "Duration of Noncompliance", false], ["risk", "Risk Assessment", false],
  ["description", "Description of the Noncompliance", true], ["reliability_impact", "Potential Impact to the BPS", true],
  ["root_cause", "Root Cause", true], ["mitigation", "Mitigating Activities", true],
  ["triage_notes", "Additional Information / Triage Notes", true],
  ["contact_name", "Entity Contact Name", false], ["contact_email", "Entity Contact Email", false],
];

function SelfReportDialog({ pncId, onClose }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/pnc/${pncId}/self-report/prefill`).then(({ data }) => setF(data))
      .catch(() => { toast.error("Couldn't load the self-report draft."); onClose(); });
  }, [pncId]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const exportWord = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/pnc/${pncId}/self-report/docx`, f, { responseType: "blob" });
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : "Self_Report.docx";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("Self-report exported to Word.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Export failed."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pnc-self-report-dialog">
        <DialogHeader><DialogTitle>NERC Self-Report — editable draft</DialogTitle></DialogHeader>
        {!f ? <p className="text-sm text-muted-foreground py-4">Loading draft…</p> : (
          <div className="space-y-3">
            <p className="text-[11px] text-muted-foreground">Pre-filled from this PNC, the originating incident's triage notes, and your entity profile. Edit anything, then export to Word to finish and submit to your Regional Entity.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {SR_FIELDS.filter(([, , area]) => !area).map(([k, label]) => (
                <Field key={k} label={label}><Input value={f[k] || ""} onChange={(e) => set(k, e.target.value)} className="rounded-none" data-testid={`sr-${k}`} /></Field>
              ))}
            </div>
            {SR_FIELDS.filter(([, , area]) => area).map(([k, label]) => (
              <Field key={k} label={label}><Textarea value={f[k] || ""} onChange={(e) => set(k, e.target.value)} rows={3} className="rounded-none" data-testid={`sr-${k}`} /></Field>
            ))}
            <div className="flex justify-end gap-2 border-t border-border pt-3">
              <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
              <Button className="rounded-none" onClick={exportWord} disabled={busy} data-testid="sr-export-word"><DownloadSimple size={16} className="mr-1.5" /> {busy ? "Exporting…" : "Export to Word"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function NopLibrary() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const load = () => api.get("/pnc/nop/filings").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/pnc/nop/upload", fd);
      toast.success(`Imported ${data.rows} enforcement rows from ${data.source_file}.`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed."); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const del = async (batchId) => {
    try { await api.delete(`/pnc/nop/filings/${batchId}`); toast.success("Filing removed."); load(); }
    catch (e) { toast.error("Couldn't remove that filing."); }
  };

  return (
    <div className="space-y-4" data-testid="nop-library">
      <div className="border border-border bg-white p-5">
        <div className="flex items-center gap-2 mb-2"><FileXls size={20} className="text-primary" /><h3 className="font-display font-bold">NOP Benchmark Library</h3></div>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Upload NERC's monthly/quarterly <span className="font-semibold">Notice of Penalty (spreadsheet NOP)</span> files
          (from the NERC Enforcement Actions page). ClaroShield reads the Reliability Standard, requirement, penalty
          amount, risk and disposition so it can benchmark your own PNC records against comparable public cases.
          CIP records are anonymized by NERC but still usable for penalty ranges.
        </p>
        <div className="flex gap-2 mt-4">
          <Button className="rounded-none" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="nop-upload-btn"><UploadSimple size={16} className="mr-1.5" /> {busy ? "Uploading…" : "Upload NOP spreadsheet (.xlsx)"}</Button>
          <Button variant="outline" className="rounded-none" onClick={load} data-testid="nop-refresh"><ArrowClockwise size={16} /></Button>
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="nop-upload-input" />
        </div>
      </div>

      <div className="border border-border bg-white p-5">
        <div className="text-sm font-semibold mb-3">Uploaded filings {data && <span className="text-muted-foreground font-normal">· {data.total} total records</span>}</div>
        {!data || data.batches.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="nop-empty">No filings uploaded yet.</p>
        ) : (
          <div className="space-y-2">
            {data.batches.map((b) => (
              <div key={b.batch_id} className="flex items-center justify-between border border-border p-3" data-testid={`nop-batch-${b.batch_id}`}>
                <div>
                  <div className="text-sm font-medium">{b.source_file}</div>
                  <div className="text-xs text-muted-foreground">{b.rows} records · {b.uploaded_at ? new Date(b.uploaded_at).toLocaleString() : ""}</div>
                </div>
                <button onClick={() => del(b.batch_id)} className="text-muted-foreground hover:text-red-600" data-testid={`nop-delete-${b.batch_id}`}><Trash size={16} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
